export const SIGNUP_DIAGNOSTIC_EVENT = 'auth:signup_diagnostic'
export const SIGNUP_DIAGNOSTIC_MARKER = 'signup-diagnostic-start'
export const SIGNUP_DIAGNOSTIC_TTL = 10 * 60 * 1000

export function signupRoute(path: string) {
  if (path === '/sign-up' || path.startsWith('/sign-up/')) return 'signup'
  if (path === '/sign-in' || path.startsWith('/sign-in/')) return 'signin'
  if (path === '/onboarding' || path.startsWith('/onboarding/')) return 'onboarding'
  if (path === '/browse') return 'browse'
  if (path.startsWith('/watch/')) return 'watch'
  if (path === '/') return 'home'
  return 'other'
}

export function observeSignup(
  read: () => { auth_loaded: boolean; form_present: boolean },
  capture: (properties: Record<string, string | number | boolean>) => void,
) {
  const started = Date.now()
  const sent = new Set<string>()
  const emit = (stage: string, state: ReturnType<typeof read>) => {
    if (sent.has(stage)) return
    sent.add(stage)
    try {
      capture({ diagnostic_version: 1, stage, route: 'signup', ...state })
    } catch {
      // Optional diagnostics must never interfere with authentication.
    }
  }
  const check = () => {
    try {
      const state = read()
      emit('started', state)
      if (state.auth_loaded) emit('auth_loaded', state)
      if (state.form_present) emit('form_present', state)
      if (Date.now() - started >= 30_000) {
        emit('checkpoint_30s', state)
        clearInterval(timer)
      }
    } catch {
      clearInterval(timer)
    }
  }
  const timer = setInterval(check, 1000)
  check()
  return () => clearInterval(timer)
}

// PostHog enriches events automatically. Keep only explicitly approved fields
// on this diagnostic event, including the SDK token required for ingestion.
const SAFE_PROPERTIES = new Set([
  'token',
  'distinct_id',
  '$session_id',
  '$window_id',
  '$device_id',
  '$insert_id',
  '$lib',
  '$lib_version',
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$host',
  '$is_identified',
  '$process_person_profile',
  'diagnostic_version',
  'stage',
  'route',
  'auth_loaded',
  'form_present',
  'request_kind',
  'status',
  'online',
  'visibility',
])

export function sanitizeSignupDiagnostic<
  T extends { event?: string; properties?: Record<string, unknown> } | null,
>(event: T): T {
  if (
    !event ||
    ![SIGNUP_DIAGNOSTIC_EVENT, 'diagnostic:request_failure'].includes(event.event ?? '')
  )
    return event
  return {
    ...event,
    properties: Object.fromEntries(
      Object.entries(event.properties ?? {}).filter(([key]) => SAFE_PROPERTIES.has(key)),
    ),
  }
}
